/**
 * ChangePasswordPage — Forced password change screen for users created
 * directly by an admin. Hard-gates the rest of the app until the flag
 * `public_metadata.requires_password_change` is cleared.
 *
 * Spec: docs/superpowers/specs/2026-05-13-admin-create-user-design.md
 */
import { useState } from 'react';
import axios from 'axios';
import { useAuth as useClerkAuth, useUser as useClerkUser } from '@clerk/clerk-react';
import { KeyRound, AlertCircle, CheckCircle } from 'lucide-react';
import { userService } from '../../services/userService';

export function ChangePasswordPage() {
  const { user } = useClerkUser();
  const { getToken } = useClerkAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user) {
      setError('Session expired. Please sign in again.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Single backend call performs all three operations atomically:
      //   1. Refuse if the requires_password_change flag is already cleared
      //      (defense against an attacker calling the endpoint with just the
      //      temp password to escape the gate without rotating).
      //   2. Verify the supplied current_password against Clerk's hash.
      //   3. Update the password and clear the flag.
      // Doing this server-side means the flag can never be cleared without
      // actually changing the password — single-use by construction.
      await userService.completePasswordChange(() => getToken(), {
        current_password: currentPassword,
        new_password: newPassword,
      });

      // Hard reload to /chat. A client-side navigate is unreliable here:
      // `user.reload()` mutates the Clerk user object in place rather than
      // producing a new reference, so the useEffect in AuthContext doesn't
      // re-fire and `requiresPasswordChange` stays true in React state —
      // which keeps AuthenticatedApp rendering ChangePasswordPage even after
      // navigation. A full document load forces AuthContext to re-evaluate
      // from a fresh JWT.
      window.location.assign('/chat');
    } catch (err: unknown) {
      // Backend returns either a string detail or an object detail
      // ({ code, clerk_code, message }) for policy violations.
      type DetailObj = { code?: string; clerk_code?: string; message?: string };
      const rawDetail = axios.isAxiosError(err)
        ? (err.response?.data as { detail?: string | DetailObj } | undefined)?.detail
        : undefined;
      const status = axios.isAxiosError(err) ? err.response?.status : null;
      const detailString = typeof rawDetail === 'string' ? rawDetail : undefined;
      const detailObj = rawDetail && typeof rawDetail === 'object' ? rawDetail : undefined;

      if (detailString === 'invalid_current_password' || status === 401) {
        setError('The current password is incorrect.');
      } else if (detailString === 'password_already_changed') {
        setError('Password has already been changed. Reloading…');
        // The flag is already cleared on the server — just refresh into the app.
        window.location.assign('/chat');
        return;
      } else if (detailObj?.code === 'password_policy_violation') {
        // Surface Clerk's user-facing message verbatim — it tells the user
        // exactly what's wrong (too short, found in breach, etc.).
        setError(detailObj.message || 'This password is not allowed.');
      } else if (detailString && detailString.length > 0) {
        setError(detailString);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to update password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-surface flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-brand-bg shadow-xl rounded-card overflow-hidden">
          <div className="px-6 py-5 border-b border-brand-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-brand-text">Set a new password</h1>
                <p className="text-sm text-brand-muted">
                  An administrator created this account. Please choose a personal password to continue.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {error && (
              <div className="rounded-input bg-brand-error/5 p-3 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-brand-error flex-shrink-0 mt-0.5" />
                <div className="text-sm text-brand-error">{error}</div>
              </div>
            )}

            <div>
              <label htmlFor="current-password" className="block text-sm font-medium text-brand-text mb-1">
                Temporary Password
              </label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={isSubmitting}
                className="block w-full px-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-brand-text mb-1">
                New Password
              </label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                disabled={isSubmitting}
                className="block w-full px-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-brand-text mb-1">
                Confirm New Password
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                disabled={isSubmitting}
                className="block w-full px-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-brand-surface bg-brand-primary rounded-input hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-pill h-4 w-4 border-b-2 border-brand-surface" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Set Password & Continue
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
